/**
 * @file SalesRepresentativeRoutes.tsx
 * @module app/routing/SalesRepresentativeRoutes
 * @description Defines role-specific SalesRepresentativeRoutes route mappings, protecting UTrade Admin screens and selecting the appropriate application views.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { FC, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import TopBarProgress from "react-topbar-progress-indicator";
import { getCSSVariableValue } from "../../_admin/assets/ts/_utils";
import { WithChildren } from "../../_admin/helpers";
import { MasterLayout } from "../../_admin/layout/MasterLayout";
import Dashboard from "../modules/sales-management/sales-representative/dashboard/dashboard";
import CustomerManagement from "../modules/sales-management/sales-representative/customer-management/customer-management";
import Orders from "../modules/sales-management/sales-representative/orders/orders";
import TargetTracking from "../modules/sales-management/sales-representative/target-tracking/target-management";
import Notifications from "../modules/sales-management/sales-representative/notifications";
import Reports from "../modules/sales-management/sales-representative/reports/reports";
import CustomerProfile from "../modules/customers/customer-profile";
import OrderDetails from "../modules/orders-delivery/pages/order-details/order-details";
import AddNewOrderSubAdmin from "../modules/orders-delivery/pages/operations/add-new-order-assist";
import EditOrderDetails from "../modules/orders-delivery/pages/order-details/edit-order-details";
import { PageLink, PageTitle } from "../../_admin/layout/core";

const salesManagementRepresentativeBreadCrumbs: Array<PageLink> = [
  {
    title: 'Sales Management',
    path: '/sales-management',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Sales Representative',
    path: '/sales-management/representative',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
];
const salesManagementRepresentativeOrdersBreadcrumbs: Array<PageLink> = [
  {
    title: 'Sales Management',
    path: '/sales-management',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Sales Representative',
    path: '/sales-management/representative',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Orders',
    path: '/orders',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
];
const salesManagementRepresentativeCustomerAssignmentBreadcrumbs: Array<PageLink> = [
  {
    title: 'Sales Management',
    path: '/sales-management',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Sales Representative',
    path: '/sales-management/representative',
    isSeparator: false,
    isActive: false,
  },
  {
    title: 'Customer Management',
    path: '/customers',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
];
const SalesRepresentativeRoutes = () => {
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route path="auth/*" element={<Navigate to="/sales-management/representative/dashboard" />} />
        {/* Pages */}
        <Route
          path="sales-management/representative/dashboard"
          element={
            <>
              <PageTitle breadcrumbs={salesManagementRepresentativeBreadCrumbs}>
                Dashboard
              </PageTitle>
              <Dashboard />
            </>
          }
        />
        <Route
          path="sales-management/representative/customer-management"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={salesManagementRepresentativeCustomerAssignmentBreadcrumbs}>
                Customer Management
              </PageTitle>
              <CustomerManagement />
            </SuspensedView>
          }
        />
        <Route
          path="sales-management/representative/orders"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={salesManagementRepresentativeOrdersBreadcrumbs}>
                Orders
              </PageTitle>
              <Orders />
            </SuspensedView>
          }
        />
        <Route
          path="sales-management/representative/target-tracking"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={salesManagementRepresentativeBreadCrumbs}>
                Target Tracking
              </PageTitle>
              <TargetTracking />
            </SuspensedView>
          }
        />
        {/* <Route
          path="sales-management/representative/notifications"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={salesManagementRepresentativeBreadCrumbs}>
                Notifications
              </PageTitle>
              <Notifications />
            </SuspensedView>
          }
        />
        <Route
          path="sales-management/representative/report-and-performance"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={salesManagementRepresentativeBreadCrumbs}>
                Reports and Performance
              </PageTitle>
              <Reports />
            </SuspensedView>
          }
        /> */}
        <Route
          path="customers/customer-profile"
          element={
            <SuspensedView>
              <PageTitle breadcrumbs={salesManagementRepresentativeCustomerAssignmentBreadcrumbs}>
                Customer Profile
              </PageTitle>
              <CustomerProfile />
            </SuspensedView>
          }
        />
        <Route
          path="/orders/add-new-order-assistAdmin"
          element={
            <>
              <PageTitle breadcrumbs={salesManagementRepresentativeOrdersBreadcrumbs}>
                Add New Order
              </PageTitle>
              <AddNewOrderSubAdmin />
            </>
          }
        />
        <Route
          path="/orders/order-details"
          element={
            <>
              <PageTitle breadcrumbs={salesManagementRepresentativeOrdersBreadcrumbs}>
                Order Details
              </PageTitle>
              <OrderDetails />
            </>
          }
        />
        <Route
          path="/orders/edit-order-details"
          element={
            <>
              <PageTitle breadcrumbs={salesManagementRepresentativeOrdersBreadcrumbs}>
                Edit Order Details
              </PageTitle>
              <EditOrderDetails />
            </>
          }
        />
      </Route>
    </Routes>
  );
};
const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue("--bs-primary");
  TopBarProgress.config({
    barColors: { "0": baseColor },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};
export { SalesRepresentativeRoutes };
